import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import { fetchMyOrganizations } from '../../utils/useMyOrganizations';
import { useSortableTable, SortableTh, nameFromString, sortQuery } from '../../components/SortableTable';
import { formatDate } from '../../utils/dateFormat';

// Accounting — Monthly Billing Summary: plain-English view of what can be billed, how much, what is close
// and what is missing. All billing decisions come from the Batch 2 engine on the server.
export const STATUS_STYLE = {
    ready: { bg: '#e8f5e9', fg: '#1b5e20', title: 'Ready to bill' },
    almost: { bg: '#fff4e0', fg: '#8a5a00', title: 'Almost billable' },
    blocked: { bg: '#fdecea', fg: '#b71c1c', title: 'Blocked — missing information' },
    not_billable: { bg: '#eceff1', fg: '#455a64', title: 'Not billable this month' },
};
const CLAIM_OPTIONS = [
    ['not_billed', 'Not billed'],
    ['billed', 'Billed'],
    ['paid', 'Paid'],
    ['denied', 'Denied'],
    ['written_off', 'Written off'],
];
const usd = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const day = (d) => (d ? formatDate(d) : '');
const errText = (e) => e?.response?.data?.message || e?.message || 'Request failed';

export const MonthPicker = ({ year, month, onChange }) => (
    <span style={{ fontSize: 13 }}>
        <button type="button" aria-label="Previous month" className="btn btn-sm btn-light" onClick={() => { const m = moment({ year, month: month - 1 }).subtract(1, 'month'); onChange(m.year(), m.month() + 1); }}>&lsaquo;</button>
        <b className="mx-2" data-testid="acct-month">{moment({ year, month: month - 1 }).format('MMMM YYYY')}</b>
        <button type="button" aria-label="Next month" className="btn btn-sm btn-light" onClick={() => { const m = moment({ year, month: month - 1 }).add(1, 'month'); onChange(m.year(), m.month() + 1); }}>&rsaquo;</button>
    </span>
);

const Tile = ({ kind, big, sub, testid }) => {
    const s = STATUS_STYLE[kind] || STATUS_STYLE.not_billable;
    return (
        <div className="col-md-3 col-sm-6 mb-3" data-testid={testid}>
            <div style={{ background: s.bg, color: s.fg, borderRadius: 8, padding: '14px 16px', height: '100%' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{s.title === 'Blocked — missing information' ? 'Missing information' : s.title}</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{big}</div>
                <div style={{ fontSize: 12 }}>{sub}</div>
            </div>
        </div>
    );
};

export const StatusBadge = ({ row }) => {
    const s = STATUS_STYLE[row.status] || STATUS_STYLE.not_billable;
    return (
        <span title={row.explanation} data-testid="acct-status" style={{ background: s.bg, color: s.fg, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {row.statusLabel}
        </span>
    );
};

export const Items = ({ items }) => (
    <ul className="list-unstyled mb-0" style={{ fontSize: 13 }}>
        {items.map((i) => (
            <li key={i.code}>
                {i.label}{i.units > 1 ? ` ×${i.units}` : ''} <small style={{ color: '#9e9e9e' }}>({i.code})</small>
            </li>
        ))}
    </ul>
);

const pct = (n) => `${Number(n)}%`;
export const Arithmetic = ({ row }) => (
    <div data-testid="acct-arithmetic">
        <table className="table table-sm table-bordered mb-1" style={{ fontSize: 12, background: '#fff' }}>
            <thead><tr><th>Item</th><th className="text-right">Units</th><th className="text-right">Rate</th><th>Rate source</th><th className="text-right">Allowed (gross)</th><th>Share</th><th className="text-right">Sequestration</th><th className="text-right">Expected from payer</th><th className="text-right">Patient share</th></tr></thead>
            <tbody>
                {row.items.map((i) => (
                    <tr key={i.code}>
                        <td>{i.label} <small className="text-muted">({i.code})</small></td>
                        <td className="text-right">{i.units}</td>
                        <td className="text-right">{usd(i.rate)}</td>
                        <td>{i.rate == null ? <span className="text-danger">No rate loaded — {i.noRateReason}</span> : i.sourceLabel}{i.sourceDetail && <small className="d-block text-muted">{i.sourceDetail}</small>}</td>
                        <td className="text-right">{i.rate == null ? '—' : `${usd(i.rate)} × ${i.units} = ${usd(i.allowed)}`}</td>
                        <td>{i.rate == null ? '—' : <>payer {pct(i.payerPercent)} = {usd(i.payerBeforeSequestration)} · patient {pct(i.patientPercent)}<small className="d-block text-muted">{i.shareSource}</small></>}</td>
                        <td className="text-right">{i.rate == null ? '—' : (i.sequestration ? `− ${usd(i.sequestration)} (${pct(i.sequestrationPercent)})` : usd(0))}</td>
                        <td className="text-right">{usd(i.expectedFromPayer)}</td>
                        <td className="text-right">{usd(i.patientShare)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <small className="text-muted">Estimate — not a claim amount.{row.patientShareNote ? ` ${row.patientShareNote}.` : ''}{row.moneyFlag ? ` ${row.moneyFlag}: counted, excluded from $ totals.` : ''}</small>
    </div>
);

const REVENUE_SORT_COLUMNS = {
    practice: { type: 'string', value: (p) => p?.practice },
    expected: { type: 'number', value: (p) => p?.expectedCollections, defaultDir: 'desc' },
    forecast: { type: 'number', value: (p) => p?.tdwForecast, defaultDir: 'desc' },
    share: { type: 'number', value: (p) => p?.practiceShare, defaultDir: 'desc' },
    actual: { type: 'number', value: (p) => p?.actual, defaultDir: 'desc' },
    variance: { type: 'number', value: (p) => p?.variance },
};

const RevenuePanel = ({ qs }) => {
    const [rev, setRev] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        axios.get(`${Prod01}/accounting/revenue?${qs}`).then((r) => { setRev(r.data); setError(null); }).catch((e) => { setRev(null); setError(errText(e)); });
    }, [qs]);
    const revSort = useSortableTable(rev?.practices || [], { id: 'accounting.revenue', columns: REVENUE_SORT_COLUMNS });
    if (error) return <div className="alert alert-warning mt-4">{error}</div>;
    if (!rev) return null;
    return (
        <div className="mt-4" data-testid="acct-revenue">
            <h6>TDW revenue forecast <small className="text-muted">— {rev.label} · visible to Super Admin and accounting only · <Link to="/accounting/practice-settings">practice agreements</Link></small></h6>
            <table className="table table-sm" style={{ fontSize: 13 }}>
                <thead><tr><SortableTh sortKey="practice" sort={revSort}>Practice</SortableTh><th>Agreement</th><SortableTh sortKey="expected" sort={revSort} className="text-right">Expected collections</SortableTh><SortableTh sortKey="forecast" sort={revSort} className="text-right">TDW forecast</SortableTh><SortableTh sortKey="share" sort={revSort} className="text-right">Practice share</SortableTh><SortableTh sortKey="actual" sort={revSort} className="text-right">Actual (from Paid)</SortableTh><SortableTh sortKey="variance" sort={revSort} className="text-right">Variance</SortableTh></tr></thead>
                <tbody>
                    {revSort.rows.map((p) => (
                        <tr key={p.organizationId || p.practice} data-testid="rev-row" title={p.formula}>
                            <td>{p.practice}<small className="d-block text-muted">{p.billablePatientMonths} billable patient-months</small></td>
                            <td>{p.agreement.feeModel === 'flat_per_patient_month' ? `${usd(p.agreement.flatAmount)} / patient-month` : `TDW ${p.agreement.tdwPercent}% / practice ${p.agreement.practicePercent}%`}<small className="d-block text-muted">patient collection {p.agreement.patientCollectionRate}%{p.agreement.isDefault ? ' · default' : ''}</small></td>
                            <td className="text-right">{usd(p.expectedCollections)}</td>
                            <td className="text-right">{usd(p.tdwForecast)}</td>
                            <td className="text-right">{usd(p.practiceShare)}</td>
                            <td className="text-right">{usd(p.actual)}</td>
                            <td className="text-right">{usd(p.variance)}</td>
                        </tr>
                    ))}
                    <tr style={{ fontWeight: 600 }}><td colSpan={2}>Total</td><td className="text-right">{usd(rev.totals.expectedCollections)}</td><td className="text-right">{usd(rev.totals.tdwForecast)}</td><td className="text-right">{usd(rev.totals.practiceShare)}</td><td className="text-right">{usd(rev.totals.actual)}</td><td className="text-right">{usd(rev.totals.variance)}</td></tr>
                </tbody>
            </table>
        </div>
    );
};

const MoneyTile = ({ title, big, sub, testid }) => (
    <div className="col-md-3 col-sm-6 mb-3" data-testid={testid}>
        <div style={{ background: '#e3f2fd', color: '#0d47a1', borderRadius: 8, padding: '10px 14px', height: '100%' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{big}</div>
            <div style={{ fontSize: 12 }}>{sub}</div>
        </div>
    </div>
);

const MoneyCell = ({ row, k }) => {
    if (!row.items.length) return '—';
    if (!row.forecastIncluded) return <small className="text-danger">{row.moneyFlag || 'excluded'}</small>;
    return usd(row[k]);
};

export const ClaimCell = ({ row, canEdit, year, month, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ status: row.claim.status, billedAt: '', paidAt: '', paidAmount: '', deniedReason: '' });
    const [error, setError] = useState(null);
    const c = row.claim;
    let detail = '';
    if (c.status === 'billed') detail = day(c.billedAt);
    if (c.status === 'paid') detail = `${usd(c.paidAmount)} on ${day(c.paidAt)}`;
    if (c.status === 'denied') detail = c.deniedReason;
    const save = async () => {
        setError(null);
        try {
            await axios.post(`${Prod01}/accounting/claims/${row.patientId}`, { year, month, program: row.program, ...form });
            setOpen(false);
            onSaved();
        } catch (e) { setError(errText(e)); }
    };
    return (
        <div style={{ fontSize: 13 }}>
            <div data-testid="acct-claim">{c.label}{detail ? <small className="d-block text-muted">{detail}</small> : null}</div>
            {canEdit && !open && <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpen(true)}>Update</button>}
            {canEdit && open && (
                <div className="mt-1" style={{ minWidth: 180 }}>
                    <select aria-label="Claim status" className="form-control form-control-sm mb-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        {CLAIM_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {['billed', 'paid', 'denied'].includes(form.status) && <input aria-label="Billed date" type="date" className="form-control form-control-sm mb-1" value={form.billedAt} onChange={(e) => setForm({ ...form, billedAt: e.target.value })} />}
                    {form.status === 'paid' && <input aria-label="Paid date" type="date" className="form-control form-control-sm mb-1" value={form.paidAt} onChange={(e) => setForm({ ...form, paidAt: e.target.value })} />}
                    {form.status === 'paid' && <input aria-label="Paid amount" type="number" min="0" step="0.01" placeholder="Amount paid" className="form-control form-control-sm mb-1" value={form.paidAmount} onChange={(e) => setForm({ ...form, paidAmount: e.target.value })} />}
                    {form.status === 'denied' && <input aria-label="Denied reason" placeholder="Reason" className="form-control form-control-sm mb-1" value={form.deniedReason} onChange={(e) => setForm({ ...form, deniedReason: e.target.value })} />}
                    {error && <small className="text-danger d-block">{error}</small>}
                    <button type="button" className="btn btn-primary btn-sm mr-1" onClick={save}>Save</button>
                    <button type="button" className="btn btn-light btn-sm" onClick={() => setOpen(false)}>Cancel</button>
                </div>
            )}
        </div>
    );
};

const STATUS_RANK = ['ready', 'almost', 'blocked', 'not_billable'];
const ACCOUNTING_SORT_COLUMNS = {
    status: { type: 'number', value: (r) => (STATUS_RANK.indexOf(r?.status) + 1) || null },
    patient: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    emrId: { type: 'string', value: (r) => r?.emrId },
    practice: { type: 'string', value: (r) => r?.practice },
    provider: { type: 'name', value: (r) => nameFromString(r?.provider) },
    payer: { type: 'string', value: (r) => r?.payer },
    amount: { type: 'number', value: (r) => r?.amount || 0, defaultDir: 'desc' },
    expected: { type: 'number', value: (r) => r?.expectedFromPayer, defaultDir: 'desc' },
    patientShare: { type: 'number', value: (r) => r?.patientShare, defaultDir: 'desc' },
    variance: { type: 'number', value: (r) => r?.variance },
};

const AccountingMonthly = () => {
    const { user } = useSelector((state) => state.auth);
    const [year, setYear] = useState(moment().year());
    const [month, setMonth] = useState(moment().month() + 1);
    const [orgs, setOrgs] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [expanded, setExpanded] = useState({});

    useEffect(() => { fetchMyOrganizations().then((d) => setOrgs(d)); }, []);
    const qs = `year=${year}&month=${month}${orgId ? `&organizationId=${orgId}` : ''}`;
    const load = useCallback(async () => {
        setError(null);
        setLoading(true);
        try { setData((await axios.get(`${Prod01}/accounting/monthly?${qs}`)).data); } catch (e) { setError(errText(e)); setData(null); }
        setLoading(false);
    }, [qs]);
    useEffect(() => { load(); }, [load]);

    const download = async () => {
        setError(null);
        try {
            const res = await axios.get(`${Prod01}/accounting/monthly/export?${qs}${tableSort.sortKey ? `&${sortQuery(tableSort.sortKey, tableSort.sortDir)}` : ''}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `accounting-${year}-${String(month).padStart(2, '0')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { setError(errText(e)); }
    };

    const filteredRows = useMemo(() => (data?.rows || []).filter((r) => filter === 'all' || r.status === filter), [data, filter]);
    const tableSort = useSortableTable(filteredRows, { id: 'accounting.monthly', columns: ACCOUNTING_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    const rows = tableSort.rows;
    const s = data?.summary;
    const dq = data?.dataQuality;
    const orgList = orgs?.organizations || [];

    return (
        <RoleShell title="Accounting — Monthly Billing Summary">
            <div className="container-fluid mt-3" data-testid="accounting-monthly">
                <div className="d-flex flex-wrap align-items-center mb-3">
                    <h5 className="mb-0 mr-3">Accounting — Monthly Billing Summary</h5>
                    <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
                    {(orgs?.global || orgList.length > 1) && (
                        <select aria-label="Practice" className="form-control form-control-sm ml-3" style={{ width: 240 }} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                            <option value="">{orgs?.global ? 'All practices' : 'All my practices'}</option>
                            {orgList.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
                        </select>
                    )}
                    <button type="button" className="btn btn-outline-primary btn-sm ml-auto" onClick={download} disabled={!data}>Download Excel</button>
                </div>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {loading && !data && <p>Loading…</p>}
                {s && (
                    <>
                        <div className="row">
                            <Tile kind="ready" testid="tile-ready" big={usd(s.ready.amount)} sub={`${s.ready.patients} patient${s.ready.patients === 1 ? '' : 's'}${s.ready.estimated ? ' · includes estimates' : ''}`} />
                            <Tile kind="almost" testid="tile-almost" big={`${s.almost.patients} patient${s.almost.patients === 1 ? '' : 's'}`} sub={s.almost.whatIsMissing.join(' · ') || 'Nothing close right now'} />
                            <Tile kind="not_billable" testid="tile-not-billable" big={s.notBillable.patients} sub="patients with nothing to bill this month" />
                            <Tile kind="blocked" testid="tile-missing" big={s.missingInfo.records} sub={`records missing information · ${s.blocked.patients} blocked (${usd(s.blocked.amountOnHold)} on hold)`} />
                        </div>
                        {s.money && (
                            <div className="row" data-testid="acct-money">
                                <MoneyTile testid="tile-allowed" title="Allowed (gross)" big={usd(s.money.allowed)} sub={`${s.money.rows} ready rows · ${s.money.label}`} />
                                <MoneyTile testid="tile-expected" title="Expected from payer" big={usd(s.money.expectedFromPayer)} sub={`after ${usd(s.money.sequestration)} Medicare sequestration`} />
                                <MoneyTile testid="tile-patient" title="Patient share" big={usd(s.money.patientShare)} sub={`${s.money.secondaryPayerRows} with a secondary payer · excluded: ${s.money.blockedInsurance.patients} missing insurance, ${s.money.noRate.patients} no rate loaded`} />
                                <MoneyTile testid="tile-expected-vs-paid" title="Expected vs Paid" big={usd(s.expectedVsPaid.variance)} sub={`${s.expectedVsPaid.rows} paid rows: expected ${usd(s.expectedVsPaid.expected)}, paid ${usd(s.expectedVsPaid.paid)}`} />
                            </div>
                        )}
                        <p className="text-muted" style={{ fontSize: 12 }}>{data.estimateNote} {data.disclaimer}</p>
                        <div className="d-flex align-items-center mb-2" style={{ fontSize: 13 }}>
                            <span className="mr-2">Show:</span>
                            {['all', 'ready', 'almost', 'blocked', 'not_billable'].map((k) => (
                                <button type="button" key={k} className={`btn btn-sm mr-1 ${filter === k ? 'btn-primary' : 'btn-light'}`} onClick={() => setFilter(k)}>
                                    {k === 'all' ? 'All' : STATUS_STYLE[k].title.replace(' — missing information', '')}
                                </button>
                            ))}
                            <span className="ml-3 mr-2">Sort by:</span>
                            <select aria-label="Sort by" className="form-control form-control-sm" style={{ width: 140 }} value={tableSort.sortKey || 'status'} onChange={(e) => tableSort.setSort({ key: e.target.value, dir: ACCOUNTING_SORT_COLUMNS[e.target.value]?.defaultDir || 'asc' })}>
                                <option value="status">Status</option>
                                <option value="patient">Patient</option>
                                <option value="amount">Allowed (gross)</option>
                            </select>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm table-hover" data-testid="acct-table">
                                <thead>
                                    <tr>
                                        <SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh><SortableTh sortKey="emrId" sort={tableSort}>EHR ID</SortableTh>
                                        <SortableTh sortKey="practice" sort={tableSort}>Practice</SortableTh><SortableTh sortKey="provider" sort={tableSort}>Billing provider</SortableTh>
                                        <SortableTh sortKey="payer" sort={tableSort}>Payer</SortableTh><SortableTh sortKey="status" sort={tableSort}>Status</SortableTh><th>Billable items</th>
                                        <SortableTh sortKey="amount" sort={tableSort} className="text-right">Allowed (gross)</SortableTh>
                                        <SortableTh sortKey="expected" sort={tableSort} className="text-right">Expected from payer</SortableTh>
                                        <SortableTh sortKey="patientShare" sort={tableSort} className="text-right">Patient share</SortableTh><th>Claim status</th>
                                        <SortableTh sortKey="variance" sort={tableSort} className="text-right">Paid vs expected</SortableTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <React.Fragment key={r.key}>
                                            <tr data-testid="acct-row" style={{ cursor: 'pointer' }} onClick={() => setExpanded({ ...expanded, [r.key]: !expanded[r.key] })}>
                                                <td>{r.patientName}<small className="d-block text-muted">{r.programLabel}</small></td>
                                                <td>{r.emrId || <span className="text-danger">missing</span>}</td>
                                                <td>{r.practice || <span className="text-danger">missing</span>}</td>
                                                <td>{r.provider || <span className="text-danger">missing</span>}</td>
                                                <td>{r.payer || <span className="text-danger">missing</span>}</td>
                                                <td><StatusBadge row={r} /></td>
                                                <td>{r.items.length ? <Items items={r.items} /> : '—'}</td>
                                                <td className="text-right"><MoneyCell row={r} k="allowed" />{r.amountIsEstimate && r.forecastIncluded && <small className="d-block text-muted">estimate</small>}</td>
                                                <td className="text-right"><MoneyCell row={r} k="expectedFromPayer" /></td>
                                                <td className="text-right"><MoneyCell row={r} k="patientShare" />{r.secondaryPayer && r.forecastIncluded && <small className="d-block text-muted" title={r.patientShareNote}>secondary payer</small>}</td>
                                                <td onClick={(e) => e.stopPropagation()}><ClaimCell row={r} canEdit={data.canEditClaims} year={year} month={month} onSaved={load} /></td>
                                                <td className="text-right" data-testid="acct-variance">{r.variance == null ? '—' : usd(r.variance)}</td>
                                            </tr>
                                            {expanded[r.key] && (
                                                <tr data-testid="acct-explain"><td colSpan={12} style={{ background: '#fafafa', fontSize: 13 }}>{r.explanation}{r.items.length > 0 && <Arithmetic row={r} />}</td></tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {!rows.length && <tr><td colSpan={12} className="text-center text-muted">No patients in this view.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        {data.canSeeRevenue && <RevenuePanel qs={qs} />}
                        {dq && (
                            <div className="mt-4" data-testid="acct-data-quality">
                                <h6>Missing information to fix</h6>
                                <p className="text-muted" style={{ fontSize: 12 }}>
                                    {Object.keys(dq.counts).map((k) => `${dq.labels[k].charAt(0).toUpperCase()}${dq.labels[k].slice(1)}: ${dq.counts[k]}`).join(' · ')}
                                    {user?.role === 'accounting' ? ' — the office manager or owner updates these on the patient record.' : ''}
                                </p>
                                {dq.records.length ? (
                                    <table className="table table-sm" style={{ fontSize: 13 }}>
                                        <thead><tr><th>Patient</th><th>EHR ID</th><th>Practice</th><th>Missing</th></tr></thead>
                                        <tbody>
                                            {dq.records.map((r) => <tr key={r.patientId}><td>{r.patientName}</td><td>{r.emrId || '—'}</td><td>{r.practice || '—'}</td><td>{r.missing.join(', ')}</td></tr>)}
                                        </tbody>
                                    </table>
                                ) : <p style={{ fontSize: 13 }}>Nothing missing for this month.</p>}
                            </div>
                        )}
                    </>
                )}
            </div>
        </RoleShell>
    );
};

export default AccountingMonthly;
